import { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "n8n-embedded-chat-interface-script";
const CHAT_SCRIPT_SRC =
  "https://cdn.jsdelivr.net/npm/n8n-embedded-chat-interface@latest/output/index.js";
const CHAT_HOST_FALLBACK =
  "https://n8n.automizehub.ai/webhook/dc51c98f-633b-4849-9b85-ccbd74f7fbc0/chat";
const WHATSAPP_URL =
  "https://wa.me/919978623133?text=Hello%20SwiftMaze%20Tech%2C%20I%20want%20to%20know%20more%20about%20your%20services.";
const MOBILE_FLOATING_BOTTOM = "112px";

const getLauncherElement = (shadowRoot) =>
  shadowRoot?.querySelector(
    ".fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary"
  ) ||
  Array.from(shadowRoot?.querySelectorAll("div") || []).find((element) => {
    const classes = element.className?.toString?.() || "";
    return (
      classes.includes("bottom-4") &&
      classes.includes("right-4") &&
      classes.includes("size-14") &&
      classes.includes("rounded-full") &&
      classes.includes("bg-primary")
    );
  });

const getCloseButton = (shadowRoot) =>
  shadowRoot?.querySelector('[aria-label="Close"]') ||
  shadowRoot?.querySelector('[aria-label="close"]') ||
  shadowRoot?.querySelector('[title="Close"]') ||
  shadowRoot?.querySelector('[title="close"]') ||
  Array.from(shadowRoot?.querySelectorAll("button") || []).find((button) =>
    /close|×|x/i.test(button.textContent || button.getAttribute("aria-label") || "")
  );

const ChatWithSwiftmaze = () => {
  const chatRef = useRef(null);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const [isFloatingMenuOpen, setIsFloatingMenuOpen] = useState(false);

  const handleChatOptionClick = () => {
    const launcherElement = getLauncherElement(chatRef.current?.shadowRoot);
    if (launcherElement) {
      launcherElement.click();
    }
    setIsFloatingMenuOpen(false);
  };

  const handleMobileChatClose = () => {
    const chatElement = chatRef.current;
    const shadowRoot = chatElement?.shadowRoot;
    const closeButton = getCloseButton(shadowRoot);
    const launcherElement = getLauncherElement(shadowRoot);

    if (closeButton) {
      closeButton.click();
    } else if (launcherElement) {
      launcherElement.click();
    } else {
      chatElement?.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    }

    window.setTimeout(() => setIsMobileChatOpen(false), 150);
  };

  useEffect(() => {
    const chatElement = chatRef.current;
    if (!chatElement) return undefined;

    const hostname =
      process.env.REACT_APP_N8N_CHATBOT_WEBHOOK_URL?.trim() || CHAT_HOST_FALLBACK;

    chatElement.setAttribute("label", "Chat with Swiftmaze Tech");
    chatElement.setAttribute("open-on-start", "false");
    chatElement.setAttribute("hostname", hostname);

    // Keep chatbot at right side and above common floating controls.
    chatElement.style.position = "fixed";
    chatElement.style.right = "20px";
    chatElement.style.bottom = "20px";
    chatElement.style.zIndex = "1001";

    let styleObserver;
    let styleInterval;

    const isMobileViewport = () => window.matchMedia("(max-width: 767px)").matches;

    const updateMobileChatState = () => {
      const shadowRoot = chatElement.shadowRoot;
      if (!shadowRoot || !isMobileViewport()) {
        setIsMobileChatOpen(false);
        return;
      }

      const hasChatComposer =
        shadowRoot.querySelector('input[placeholder*="Ask"], textarea[placeholder*="Ask"]') ||
        shadowRoot.textContent?.includes("What can I help with?");

      setIsMobileChatOpen(Boolean(hasChatComposer));
    };

    const applyShadowTheme = () => {
      const shadowRoot = chatElement.shadowRoot;
      if (!shadowRoot) return false;

      const launcherElement = getLauncherElement(shadowRoot);

      if (launcherElement) {
        launcherElement.style.setProperty("width", "48px", "important");
        launcherElement.style.setProperty("height", "48px", "important");
        launcherElement.style.setProperty("min-width", "48px", "important");
        launcherElement.style.setProperty("min-height", "48px", "important");
        launcherElement.style.setProperty("right", "20px", "important");
        launcherElement.style.setProperty(
          "bottom",
          window.matchMedia("(max-width: 767px)").matches ? MOBILE_FLOATING_BOTTOM : "20px",
          "important"
        );
        launcherElement.style.setProperty("opacity", "0", "important");
        launcherElement.style.setProperty("visibility", "hidden", "important");
        launcherElement.style.setProperty("pointer-events", "none", "important");

        launcherElement
          .querySelectorAll("span, svg")
          .forEach((element) => {
            element.style.setProperty("width", "22px", "important");
            element.style.setProperty("height", "22px", "important");
            element.style.setProperty("font-size", "22px", "important");
          });

        launcherElement
          .querySelectorAll(":scope > span")
          .forEach((element) => {
            element.style.setProperty("display", "flex", "important");
            element.style.setProperty("align-items", "center", "important");
            element.style.setProperty("justify-content", "center", "important");
            element.style.setProperty("width", "100%", "important");
            element.style.setProperty("height", "100%", "important");
            element.style.setProperty("line-height", "1", "important");
            element.style.setProperty("transform", "none", "important");
          });
      }

      const floatingCloseButton =
        shadowRoot.querySelector('[aria-label="Close"]') ||
        shadowRoot.querySelector('[aria-label="close"]') ||
        shadowRoot.querySelector('[title="Close"]') ||
        shadowRoot.querySelector('[title="close"]') ||
        Array.from(shadowRoot.querySelectorAll("button")).find((button) =>
          /close|×|x/i.test(button.textContent || button.getAttribute("aria-label") || "")
        );

      if (floatingCloseButton) {
        floatingCloseButton.style.setProperty("display", "flex", "important");
        floatingCloseButton.style.setProperty("align-items", "center", "important");
        floatingCloseButton.style.setProperty("justify-content", "center", "important");
        floatingCloseButton.style.setProperty("opacity", "1", "important");
        floatingCloseButton.style.setProperty("visibility", "visible", "important");
        floatingCloseButton.style.setProperty("pointer-events", "auto", "important");
      }

      updateMobileChatState();

      const existingStyle = shadowRoot.getElementById("swiftmaze-chat-theme");
      if (existingStyle) {
        if (existingStyle.nextSibling) {
          shadowRoot.appendChild(existingStyle);
        }
        return true;
      }

      const style = document.createElement("style");
      style.id = "swiftmaze-chat-theme";
      style.textContent = `
        .bg-primary,
        .chat-header,
        header {
          background: #3E54FF !important;
          color: #ffffff !important;
        }

        .bg-primary *,
        .chat-header *,
        header * {
          color: #ffffff !important;
        }

        .fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary {
          width: 48px !important;
          height: 48px !important;
          min-width: 48px !important;
          min-height: 48px !important;
          right: 20px !important;
          bottom: 20px !important;
        }

        .fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary span,
        .fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary svg {
          width: 22px !important;
          height: 22px !important;
          font-size: 22px !important;
        }

        .fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary > span {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          height: 100% !important;
          line-height: 1 !important;
          transform: none !important;
        }

        @media (max-width: 767px) {
          .fixed.bottom-4.right-4.size-14.cursor-pointer.rounded-full.bg-primary {
            right: 20px !important;
            bottom: ${MOBILE_FLOATING_BOTTOM} !important;
          }

          button[aria-label="Close"],
          button[aria-label="close"],
          button[title="Close"],
          button[title="close"] {
            display: flex !important;
            opacity: 1 !important;
            visibility: visible !important;
            pointer-events: auto !important;
          }
        }
      `;

      shadowRoot.appendChild(style);
      return true;
    };

    const setupShadowStyling = () => {
      styleInterval = window.setInterval(() => {
        applyShadowTheme();
        updateMobileChatState();
      }, 300);

      styleObserver = new MutationObserver(() => {
        applyShadowTheme();
        updateMobileChatState();
      });

      styleObserver.observe(chatElement, { childList: true, subtree: true });
      window.addEventListener("resize", updateMobileChatState);
    };

    let script = document.getElementById(SCRIPT_ID);

    const onScriptReady = () => {
      setupShadowStyling();
    };

    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = CHAT_SCRIPT_SRC;
      script.async = true;
      script.addEventListener("load", onScriptReady);
      document.body.appendChild(script);
    } else {
      onScriptReady();
    }

    return () => {
      if (styleObserver) styleObserver.disconnect();
      if (styleInterval) window.clearInterval(styleInterval);
      window.removeEventListener("resize", updateMobileChatState);
      if (script) {
        script.removeEventListener("load", onScriptReady);
      }
    };
  }, []);

  return (
    <>
      <div className={`swiftmaze-floating-actions ${isFloatingMenuOpen ? "is-open" : ""}`}>
        <div className="swiftmaze-floating-options" aria-hidden={!isFloatingMenuOpen}>
          <a
            className="swiftmaze-floating-option swiftmaze-floating-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label="Chat on WhatsApp"
            tabIndex={isFloatingMenuOpen ? 0 : -1}
            onClick={() => setIsFloatingMenuOpen(false)}
          >
            <span className="fa fa-whatsapp" aria-hidden="true"></span>
          </a>
          <button
            type="button"
            className="swiftmaze-floating-option swiftmaze-floating-chat"
            aria-label="Chat with SwiftMaze"
            tabIndex={isFloatingMenuOpen ? 0 : -1}
            onClick={handleChatOptionClick}
          >
            <span className="fa fa-question" aria-hidden="true"></span>
          </button>
        </div>
        <button
          type="button"
          className="swiftmaze-floating-toggle"
          aria-label={isFloatingMenuOpen ? "Close contact options" : "Open contact options"}
          aria-expanded={isFloatingMenuOpen}
          onClick={() => setIsFloatingMenuOpen((isOpen) => !isOpen)}
        >
          {isFloatingMenuOpen ? (
            <span className="fa fa-times" aria-hidden="true"></span>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
            </svg>
          )}
        </button>
      </div>
      <n8n-embedded-chat-interface ref={chatRef}></n8n-embedded-chat-interface>
      <button
        type="button"
        className={`swiftmaze-chat-mobile-close ${isMobileChatOpen ? "is-visible" : ""}`}
        onClick={handleMobileChatClose}
        aria-label="Close chatbot"
      >
        <span className="fa fa-times" aria-hidden="true"></span>
      </button>
    </>
  );
};

export default ChatWithSwiftmaze;
